import { useQuery } from '@apollo/client';
import { Box, DialogActions, DialogContent, Divider, styled } from '@mui/material';
import FlexBox from 'Components/FlexBox';
import DropDown from 'Components/Forms/DropDown';
import Loader from 'Components/Forms/Loader';
import TextInputField from 'Components/Forms/TextInputField';
import {
  CardContainer,
  DialogDefault,
  FieldWrapper,
  InfoTooltip,
  ThemeButton,
  ThemeTypography,
} from 'DesignSystem';
import ItemSummary from 'DesignSystem/DataDisplay/ItemSummary/ItemSummary';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import useDatasetMutation from 'FeatureModules/DataImport/hooks/useDatasetMutation';
import {
  datasetFarmClusteringSchema,
  IDatasetFarmClusteringFormValues,
  IDatasetImportFormValues,
} from 'FeatureModules/DataImport/schemas/geoUpload.schema';
import {
  ESiteType,
  IBaseClusterSite,
  IBaseSite,
  IClusterSite,
} from 'FeatureModules/Site/types/site.types';
import {
  getSiteExtraInfo,
  getSiteSecondTitle,
  getSiteTitle,
  SiteTypeLabels,
} from 'FeatureModules/Site/utils/site.utils';
import { Field, Form, Formik, FormikProps } from 'formik';
import { Switch } from 'formik-mui';
import { IDatasetMappedColumn } from 'graphql/secure/mutations/types/dataset-mutation.types';
import { GET_DATASET_CLUSTERS } from 'graphql/secure/queries/dataset.queries';
import { GET_SITE, GET_SITES } from 'graphql/secure/queries/site.queries';
import useMessages from 'hooks/useMessages';
import React, { FC, useEffect, useRef } from 'react';
import { Booleanish, Tbooleanish } from 'types/booleanish.types';
import { IGraphQlConnection } from 'types/core.types';
import { IDefaultDialogProps } from 'types/dialog.types';
import { formatAndRoundNumber } from 'utils/number.utils';

type IClusterSiteOption = { title: string; value: string | null };

interface IConfirmDatasetImportDialogProps extends IDefaultDialogProps {
  dataset: {
    datasetId: string;
    mappedColumns?: IDatasetMappedColumn[];
    countryCode: string;
    rawMaterialId: string;
  };
  values: IDatasetImportFormValues & Partial<IDatasetFarmClusteringFormValues>;
  onProcessingStart: () => void;
  onProcessingComplete: (processedDatasetId?: string) => void;
}

const FormItem = styled('div')<{ whiteBackground?: Tbooleanish }>(({ theme, whiteBackground }) => ({
  display: 'flex',
  alignItems: 'center',
  width: 'fit-content',
  borderRadius: theme.spacing(0.5),
  backgroundColor: whiteBackground === 'true' ? '#fff' : theme.custom.themeColors.grayScale[5],
  padding: theme.spacing(0, 2),
}));

const DropdownContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  flexDirection: 'column',
  borderRadius: 6,
  padding: theme.spacing(2),
  backgroundColor: theme.custom.themeColors.grayScale[5],

  '& .MuiSelect-select': {
    background: '#fff !important',
    borderRadius: 4,
    border: `1px solid #EEEEEE`,
    padding: '6px 8px !important',
  },
}));

const StyledTooltip = styled(InfoTooltip)(() => ({
  margin: 0,
}));

const HighlightContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  alignItems: 'center',
  background: theme.custom.themeColors.accent[20],
  borderRadius: 4,
  padding: theme.spacing(2),
}));

const ConfirmDatasetImportDialog: FC<IConfirmDatasetImportDialogProps> = ({
  open,
  dataset: { datasetId, mappedColumns, countryCode, rawMaterialId },
  values: existingValues,
  onProcessingComplete,
  onProcessingStart,
  onClose,
}) => {
  const { company } = useCompanyPlanContext();
  const { setErrorMessage, setSuccessMessage } = useMessages();
  const { startDatasetProcessing } = useDatasetMutation();

  const formRef =
    useRef<FormikProps<IDatasetFarmClusteringFormValues & IDatasetImportFormValues>>(null);
  /**
   * Runs a query to get the clusters of the dataset only if there is one dataset
   */
  const { data: datasetClusters, loading: datasetClustersLoading } = useQuery<{
    datasetClusters: IClusterSite[];
  }>(GET_DATASET_CLUSTERS, {
    variables: { datasetId },
    skip: !datasetId,
  });

  /**
   * If the user wanted to add the new dataset to an existing cluster which was passed by query params, we need to fetch it to select it
   */
  const { data: defaultSelectedSiteData, loading: defaultSelectedSiteLoading } = useQuery<{
    site: IBaseSite;
  }>(GET_SITE, {
    variables: { id: existingValues.siteClusterId },
    skip: !existingValues.siteClusterId,
  });

  const { data: availableSiteClustersData, loading: availableSiteClustersLoading } = useQuery<{
    sites: IGraphQlConnection<IBaseClusterSite>;
  }>(GET_SITES, {
    variables: {
      filters: {
        siteTypes: [ESiteType.CLUSTER],
        clusterSitesType: ESiteType.FARM,
        countryCodes: [countryCode],
        rawMaterialIds: [rawMaterialId],
        ownership: null,
      },
    },
  });

  const availableSiteClusters = availableSiteClustersData?.sites.edges.map(edge => edge.node) || [];

  const initialValues = {
    ...datasetFarmClusteringSchema.default(),
    ...existingValues,
  };

  const existingDatasetClusters = [
    ...(defaultSelectedSiteData?.site.siteType === ESiteType.CLUSTER
      ? [defaultSelectedSiteData.site as IClusterSite<ESiteType.FARM>]
      : []),
    ...(datasetClusters?.datasetClusters ? datasetClusters.datasetClusters : []),
  ];
  const dropdownOptions: IClusterSiteOption[] = [
    ...existingDatasetClusters.map(cluster => ({ title: cluster.title, value: cluster.id })),
    ...(availableSiteClusters.length
      ? availableSiteClusters.map(cluster => ({ title: cluster.title, value: cluster.id }))
      : []
    )
      // Filter out clusters that are already part of the dataset to avoid confusion, since selecting them would not have any effect
      .filter(option => !existingDatasetClusters.some(cluster => cluster.id === option.value)),
    { title: 'New cluster', value: 'new-cluster' },
  ];
  /**
   * In case there are no existing cluster we set the default value to 'new-cluster'
   * This is to needed since the dropdown will not show the new cluster option if there are no existing clusters
   */
  useEffect(() => {
    if (
      !datasetClustersLoading &&
      !datasetClusters?.datasetClusters?.length &&
      !existingValues.siteClusterId
    ) {
      formRef.current?.setFieldValue('siteClusterId', 'new-cluster');
    }
  }, [datasetClustersLoading, datasetClusters, existingValues.siteClusterId]);

  const handleClose = () => {
    onClose?.();
    onProcessingComplete(undefined);
  };

  const handleSubmit = async (
    values: IDatasetImportFormValues & IDatasetFarmClusteringFormValues
  ) => {
    onProcessingStart();
    try {
      // Start the processing of the dataset again to validate the new data
      const processingResponse = await startDatasetProcessing({
        datasetId,
        mappedColumns,
        csvSeperator: ';',
        overwriteExisting: values.overwriteExisting,
        clusterSites: values.clusterSites,
        // In case we want to create new cluster we send null to the backend on the cluster id
        ...(values.clusterSites &&
          values.siteClusterId &&
          values.siteClusterId !== 'new-cluster' && { siteClusterId: values.siteClusterId }),
      });

      if (processingResponse.errors?.length) {
        setErrorMessage(processingResponse.errors.map(err => err.message).join(' '));
        onProcessingComplete(undefined);
        return;
      }

      if (processingResponse.data?.processDataset.dataset) {
        setSuccessMessage(
          'Farm(s) and plot(s) have been created or updated and are now also available in your Partners and Sites library.'
        );
      }
      onProcessingComplete(processingResponse.data?.processDataset.dataset.id);
    } catch (error) {
      onProcessingComplete(undefined);
      // eslint-disable-next-line no-console
      console.error('Something went wrong updating / validating the dataset');
      setErrorMessage(
        'Something went wrong validating the dataset. If the issue persists please contact support.'
      );
    } finally {
      onClose?.();
    }
  };

  return (
    <DialogDefault
      open={open}
      title="Confirm farm clustering"
      preventCloseOnBackground
      data-testid="confirm-farm-clustering-dialog"
      onClose={onClose}
    >
      <Formik<IDatasetFarmClusteringFormValues & IDatasetImportFormValues>
        innerRef={formRef}
        initialValues={initialValues}
        validationSchema={datasetFarmClusteringSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values, errors, setFieldValue, isValid }) => (
          <Form>
            <DialogContent>
              {datasetClustersLoading || defaultSelectedSiteLoading ? (
                <Box position="relative" height={200}>
                  <Loader />
                </Box>
              ) : (
                <Box display="flex" flexDirection="column">
                  <Box>
                    {!!existingDatasetClusters.length && (
                      <>
                        <ThemeTypography variant="BODY_MEDIUM_BOLD">
                          Some farms of this import are already part of the following origin
                          clusters:
                        </ThemeTypography>
                        {existingDatasetClusters.map(siteCluster => (
                          <CardContainer padding={1.5} mt={1} key={siteCluster.id} minWidth={500}>
                            <ItemSummary
                              image={siteCluster.image}
                              imagePlaceholder="site-placeholder"
                              title={getSiteTitle({
                                site: siteCluster,
                                farmDisplayOption: company.farmDisplayOption,
                                siteDisplayOption: company.siteDisplayOption,
                              })}
                              externalId={getSiteSecondTitle({
                                site: siteCluster,
                                farmDisplayOption: company.farmDisplayOption,
                                siteDisplayOption: company.siteDisplayOption,
                              })}
                              extraInfo={getSiteExtraInfo({
                                site: siteCluster,
                                farmDisplayOption: company.farmDisplayOption,
                                siteDisplayOption: company.siteDisplayOption,
                              })}
                              details={[
                                {
                                  icon: 'tag-filled',
                                  title:
                                    siteCluster.siteType === ESiteType.CLUSTER &&
                                    siteCluster.sitesType
                                      ? `${SiteTypeLabels[siteCluster.sitesType]} cluster`
                                      : SiteTypeLabels[siteCluster.siteType],
                                },
                                !!siteCluster.sitesType && {
                                  icon: 'farm',
                                  title:
                                    `${formatAndRoundNumber(
                                      siteCluster.sitesCount,
                                      0
                                    )} ${SiteTypeLabels[siteCluster.sitesType].toLowerCase()}${
                                      siteCluster.sitesCount <= 1 ? '' : 's'
                                    }` +
                                    (siteCluster.sitesType === ESiteType.FARM
                                      ? ` - ${formatAndRoundNumber(
                                          siteCluster.plotsCount,
                                          0
                                        )} plot${
                                          siteCluster.plotsCount !== 1 ? 's' : ''
                                        } (${formatAndRoundNumber(siteCluster.plotsSizeHa)} ha)`
                                      : ''),
                                },
                                !!siteCluster.sitesRawMaterial && {
                                  icon: 'raw-material',
                                  title: siteCluster.sitesRawMaterial.title,
                                },
                                {
                                  icon: 'location',
                                  title: siteCluster.locationName,
                                },
                              ]}
                            />
                          </CardContainer>
                        ))}

                        {existingDatasetClusters.some(
                          existingDatasetCluster =>
                            existingDatasetCluster.countryCode !== countryCode ||
                            (!existingDatasetCluster.sitesRawMaterial !== undefined &&
                              existingDatasetCluster.sitesRawMaterial?.id !== rawMaterialId)
                        ) && (
                          <HighlightContainer mt={2}>
                            <Icon name="warning" size="x-large" color="yellow-80" />
                            <ThemeTypography variant="BODY_MEDIUM_BOLD">
                              Some farms in this import are already part of an origin cluster with a
                              different country or raw material. If you add the imported farms to
                              these existing clusters, the cluster will have a mismatch of country
                              and/or raw material. If you choose a new cluster for these farms the
                              cluster country and raw material will match.
                            </ThemeTypography>
                          </HighlightContainer>
                        )}
                      </>
                    )}

                    <DropdownContainer mt={3}>
                      <FormItem whiteBackground={Booleanish(true)}>
                        <ThemeTypography variant="BODY_MEDIUM">
                          Add all imported farms to the same origin cluster:
                        </ThemeTypography>
                        <Field name="clusterSites" fullWidth component={Switch} type="checkbox" />
                        <StyledTooltip
                          variant="INFO"
                          text="Enable this to cluster all farms of each imported dataset to an origin cluster unique per country and partner. Disable this to not cluster farms and store them individually per partner. An origin cluster is a group of sites at the origin that output the same component (e.g. a cluster of cocoa farms)."
                        />
                      </FormItem>
                      {values.clusterSites && (
                        <>
                          <Box mt={1.5} mb={0.5}>
                            <ThemeTypography variant="BODY_MEDIUM_BOLD">
                              Select cluster to add imported farms into
                            </ThemeTypography>
                          </Box>
                          {availableSiteClustersLoading ? (
                            <Box position="relative" height={32}>
                              <Loader size={16} />
                            </Box>
                          ) : (
                            <DropDown
                              options={dropdownOptions || []}
                              onChange={(item: IClusterSiteOption) => {
                                setFieldValue('siteClusterId', item.value);
                              }}
                              fullWidth
                              renderItems={(item: IClusterSiteOption) => (
                                <ThemeTypography variant="BODY_SMALL">{item.title}</ThemeTypography>
                              )}
                              placeholder="Select cluster"
                              currentValue={dropdownOptions.find(
                                cluster => cluster.value === values.siteClusterId
                              )}
                              errorMsg={errors.siteClusterId}
                            />
                          )}

                          {values.siteClusterId === 'new-cluster' && (
                            <FieldWrapper label="Cluster title" required fullWidth>
                              <Field
                                name="siteClusterTitle"
                                placeholder="E.g. Afram Plains Cocoa Cooperative"
                                size="small"
                                component={TextInputField}
                              />
                            </FieldWrapper>
                          )}
                        </>
                      )}
                      <FlexBox mt={2} gap={2} flexDirection="column" alignItems="normal">
                        <HighlightContainer>
                          <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_100">
                            Avoid using a cluster to represent a shipment or batch. For managing
                            farms on a contract or import basis, it is better to use the dataset
                            itself.
                          </ThemeTypography>
                        </HighlightContainer>
                        <Divider />
                        <ThemeTypography variant="BODY_MEDIUM" color="GRAY_60">
                          All farms of imported plots will be clustered together in your selected
                          cluster. All existing farms with plots of matching plot IDs to the import
                          will also be moved to this cluster.
                        </ThemeTypography>
                      </FlexBox>
                    </DropdownContainer>
                  </Box>
                </Box>
              )}
            </DialogContent>

            <DialogActions>
              <Box display="flex" justifyContent="space-between" width="100%">
                <ThemeButton color="BLUE_ICE" disabled={isSubmitting} onClick={handleClose}>
                  Cancel
                </ThemeButton>
                <ThemeButton
                  type="submit"
                  disabled={datasetClustersLoading || !isValid}
                  loading={isSubmitting}
                  data-testid="confirm-farm-clustering-button"
                >
                  Confirm
                </ThemeButton>
              </Box>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </DialogDefault>
  );
};

export default ConfirmDatasetImportDialog;
